import { memo, useEffect, useState } from "react";
import { useTranslate } from "helpers";
import { validateYup } from "services/forms/validation/validation";
import {
  createFleetVehicleDamageAPI,
  getDamageStatusesAPI,
  getFleetVehicleDamageAPI,
  updateFleetVehicleDamageAPI,
} from "services/api/fleet/vehicles/damages/api";
import { Errors } from "services/forms/validation/types";
import {
  DamageStatus,
  FleetVehicleDamageEditFormValues,
} from "services/api/fleet/vehicles/damages/types";
import { initialFleetVehicleDamageEditForm } from "services/api/fleet/vehicles/damages/consts";
import { transformFleetVehicleDamageEditFormValues } from "services/api/fleet/vehicles/damages/transformations";
import { VehicleDamageEditProps } from "./types";
import WizardStep from "ui/components/Wizard/WizardStep";
import { Wizard } from "ui/components/Wizard";
import WizardFooter from "ui/components/Wizard/WizardFooter";
import { Loader } from "ui/components/Loader";
import { Modal, ModalIntro } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import invoiceAnimation from "ui/theme/animations/invoice.json";
import DamageBasicInfoForm from "./components/DamageBasicInfoForm";
import DamagePhotoPinForm from "./components/DamagePhotoPinForm";
import DamageDocumentForm from "./components/DamageDocumentForm";
import { FLEET_VEHICLE_DAMAGE_BASIC_INFO_EDIT_FORM_SCHEMA } from "./validation";

const VehicleDamageEditCard: React.FC<VehicleDamageEditProps> = (
  props
) => {
  const { show, vehicleId, damageId, onSuccess, onClose, onStepChange } = props;

  const { t } = useTranslate();
  const [stepCurrent, setStepCurrent] = useState(0);
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [formValues, setFormValues] =
    useState<FleetVehicleDamageEditFormValues>({
      ...initialFleetVehicleDamageEditForm,
      vehicleId,
    });
  const [statuses, setStatuses] = useState<DamageStatus[]>([]);

  // Ovisi i o `show`: kod unosa nove štete `damageId` je null i ne mijenja se, a komponenta
  // ostaje montirana nakon zatvaranja — bez toga bi se sljedeće otvaranje vratilo na zadnji
  // korak prethodnog unosa.
  useEffect(() => {
    if (!show) return;

    setFormValues({ ...initialFleetVehicleDamageEditForm, vehicleId });
    setStepCurrent(0);

    if (damageId) {
      fetchDamage(damageId);
    }
    fetchStatuses();
  }, [show, damageId, vehicleId]);

  const fetchDamage = async (id: string) => {
    if (!id) {
      setFormValues({
        ...initialFleetVehicleDamageEditForm,
        vehicleId,
      });
      return;
    }

    setIsLoading(true);

    try {
      const result = await getFleetVehicleDamageAPI(id);
      if (result) {
        const formData = transformFleetVehicleDamageEditFormValues(result);
        setFormValues(formData);
      }
    } catch {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    setErrors({});
  };

  const fetchStatuses = async () => {
    try {
      const result = await getDamageStatusesAPI();
      setStatuses(result);
    } catch {
      // ignore
    }
  };

  const handleNextStep = async () => {
    if (stepCurrent === 0) {
      const isValid = validateYup(formValues, FLEET_VEHICLE_DAMAGE_BASIC_INFO_EDIT_FORM_SCHEMA, setErrors);
      if (!isValid) return;
    }
    const next = stepCurrent + 1;
    setStepCurrent(next);
    onStepChange?.(next);
  };

  const handleBackStep = async () => {
    const prev = stepCurrent - 1;
    setStepCurrent(prev);
    onStepChange?.(prev);
  };

  const handleFinish = async () => {
    setIsLoading(true);

    try {
      const saved = damageId
        ? await updateFleetVehicleDamageAPI(
            damageId,
            formValues,
            formValues.photoUploads,
            formValues.documentUploads,
            t
          )
        : await createFleetVehicleDamageAPI(
            vehicleId,
            formValues,
            formValues.photoUploads,
            formValues.documentUploads,
            t
          );

      if (onSuccess) onSuccess(saved);
    } catch {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  const steps = [
    <WizardStep
      key={1}
      stepNumber={0}
      stepCurrent={stepCurrent}
      stepTitle="Osnovne informacije"
    >
      <DamageBasicInfoForm
        formValues={formValues}
        setFormValues={setFormValues}
        errors={errors}
      />
    </WizardStep>,
    <WizardStep
      key={2}
      stepNumber={1}
      stepCurrent={stepCurrent}
      // stepTitle="Fotografije i pinovi"
    >
      <DamagePhotoPinForm
        formValues={formValues}
        setFormValues={setFormValues}
        statuses={statuses}
      />
    </WizardStep>,
    <WizardStep
      key={3}
      stepNumber={2}
      stepCurrent={stepCurrent}
      stepTitle="Dokumentacija"
    >
      <DamageDocumentForm
        formValues={formValues}
        setFormValues={setFormValues}
      />
    </WizardStep>,
  ];

  // Intro drži polovicu ekrana, pa obrazac ostaje u čitljivom stupcu. Vrijedi za prvi i
  // zadnji korak; srednji korak treba punu širinu za fotografiju s oznakama štete.
  const showIntro = stepCurrent === 0 || stepCurrent === 2;

  return (
    <Modal
      show={show}
      onHide={onClose}
      position="side"
      size="full"
      // Intro se pali po koraku, pa bi srednji korak inače bljesnuo sivom podlogom.
      showOverlay={false}
      // Intro ide kroz Modal, ne kao zaseban portal — inače ga prekrije siva pozadina
      // koju Modal crta kad introa nema. Vidi se samo na prvom koraku.
      intro={
        showIntro
          ? {
              content: (
                <ModalIntro
                  animation={invoiceAnimation}
                  title="Prijavite štetu na vozilu!"
                  description="Unesite osnovne podatke o šteti, dodajte fotografije s označenim točkama oštećenja i priložite potrebnu dokumentaciju."
                  show={show}
                />
              ),
            }
          : undefined
      }
    >
      <ModalHeader
        icon="/media/images/add-icon.svg"
        title={
          damageId
            ? t("admin.fleet.vehicle.damage.edit.title")
            : t("admin.fleet.vehicle.damage.create.title")
        }
        onHide={onClose}
      />
      {/* Podnožje je pravi WizardFooter, pa donji razmak za "fixed" podnožje otpada. */}
      <ModalBody fixedFooterSpacing={false}>
        <Wizard steps={steps} />
        <Loader isLoading={isLoading ?? false}> </Loader>
      </ModalBody>
      <WizardFooter
        stepCurrent={stepCurrent}
        steps={steps}
        onNextStep={handleNextStep}
        onBackStep={handleBackStep}
        onFinish={handleFinish}
        isLoading={isLoading}
        isReadOnly={false}
      />
    </Modal>
  );
};

export default memo(VehicleDamageEditCard);
